(() => {
  if (window.BTAnalyticsInitialized) return;
  window.BTAnalyticsInitialized = true;

  const pageEvents = new Set();
  const debug = new URLSearchParams(window.location.search).get('bt_analytics_debug') === '1';
  const currency = 'COP';
  const pageType = () => [...document.body.classList].find((name) => name.startsWith('template-'))?.replace('template-', '') || 'unknown';

  const sessionOnce = (key) => {
    try {
      const storageKey = `bt-analytics:${key}`;
      if (sessionStorage.getItem(storageKey)) return false;
      sessionStorage.setItem(storageKey, '1');
      return true;
    } catch (error) {
      if (pageEvents.has(key)) return false;
      pageEvents.add(key);
      return true;
    }
  };

  const publish = (eventName, detail = {}, onceKey = '') => {
    if (onceKey && !sessionOnce(onceKey)) return false;
    const payload = { event_name: eventName, currency, ...detail };
    window.dataLayer?.push({ event: eventName, ...payload });
    document.dispatchEvent(new CustomEvent('bt:analytics', { detail: payload }));
    const shopifyEventName = `bt:${eventName.replace(/^bt_/, '')}`;
    try {
      window.Shopify?.analytics?.publish?.(shopifyEventName, payload)?.catch?.(() => {});
    } catch (error) {}
    if (debug) console.info(`[BT Analytics] ${JSON.stringify(payload)}`);
    return true;
  };

  window.BTAnalytics = Object.freeze({ publish });

  const money = (cents, code) => new Intl.NumberFormat('es-CO', {
    style: 'currency', currency: code || currency, maximumFractionDigits: 0,
  }).format(Math.max(0, cents) / 100);

  let cartRequest = null;
  const getCart = () => cartRequest || (cartRequest = fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
    credentials: 'same-origin', headers: { Accept: 'application/json' },
  }).then((response) => response.ok ? response.json() : Promise.reject(new Error('cart unavailable')))
    .finally(() => { cartRequest = null; }));

  const shippingZones = {
    'Antioquia':'Z1','Caldas':'Z1','Quindío':'Z1','Risaralda':'Z1','Valle del Cauca':'Z1',
    'Atlántico':'Z2','Bogotá, D.C.':'Z2','Bolívar':'Z2','Boyacá':'Z2','Cauca':'Z2','Cesar':'Z2',
    'Córdoba':'Z2','Cundinamarca':'Z2','Huila':'Z2','Magdalena':'Z2','Meta':'Z2',
    'Norte de Santander':'Z2','Santander':'Z2','Sucre':'Z2','Tolima':'Z2',
  };
  class FreeShipping extends HTMLElement {
    connectedCallback() {
      this.department = this.querySelector('[data-bt-shipping-department]');
      this.city = this.querySelector('[data-bt-shipping-city]');
      try {
        const destination = JSON.parse(sessionStorage.getItem('bt-shipping-destination-v2') || '{}');
        if (this.department) this.department.value = destination.department || '';
        if (this.city) this.city.value = destination.city || '';
      } catch (error) {}
      this.onDestination = (event) => {
        event?.stopPropagation();
        const destination = {department:this.department?.value || '',city:this.city?.value.trim() || ''};
        try { sessionStorage.setItem('bt-shipping-destination-v2',JSON.stringify(destination)); } catch(error) {}
        document.querySelectorAll('bt-free-shipping').forEach(element => {
          if(element.department) element.department.value=destination.department;
          if(element.city) element.city.value=destination.city;
          element.render(Number(element.dataset.currentSubtotalCents || element.dataset.initialSubtotalCents || 0));
        });
      };
      this.department?.addEventListener('change',this.onDestination);
      this.city?.addEventListener('input',this.onDestination);
      this.city?.addEventListener('change',this.onDestination);
      this.render(Number(this.dataset.initialSubtotalCents || 0));
      this.observe();
      getCart().then(FreeShipping.updateAll).catch(() => {});
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.department?.removeEventListener('change',this.onDestination);
      this.city?.removeEventListener('input',this.onDestination);
      this.city?.removeEventListener('change',this.onDestination);
    }
    analyticsParameters(subtotal) {
      const threshold = Number(this.dataset.thresholdCents || 0);
      const addressKnown = this.dataset.addressKnown === 'true';
      const primaryCity = this.dataset.primaryCity === 'true';
      return {
        cart_value: subtotal / 100,
        threshold: threshold / 100,
        amount_remaining: Math.max(0, threshold - subtotal) / 100,
        city_group: addressKnown ? (primaryCity ? this.dataset.zone : 'PAID') : 'UNKNOWN',
        threshold_group: addressKnown && primaryCity ? 'Z1_Z2_600K' : 'NOT_ELIGIBLE',
        source_component: this.dataset.context || 'unknown',
      };
    }

    observe() {
      if (this.observer || !window.IntersectionObserver) return;
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        const subtotal = Number(this.dataset.currentSubtotalCents || this.dataset.initialSubtotalCents || 0);
        publish('bt_free_shipping_progress_view', this.analyticsParameters(subtotal), `free-shipping-view:${location.pathname}:${this.dataset.context || 'unknown'}`);
        this.observer.disconnect();
      }, { threshold: 0.4 });
      this.observer.observe(this);
    }

    emitMilestones(subtotal, threshold) {
      if (!threshold || subtotal <= 0 || this.dataset.primaryCity !== 'true') return;
      const progress = Math.min(100, Math.max(0, subtotal / threshold * 100));
      const parameters = this.analyticsParameters(subtotal);
      [25, 50, 75].forEach((milestone) => {
        if (progress >= milestone) publish(`bt_free_shipping_progress_${milestone}`, { ...parameters, progress_percent: milestone }, `free-shipping:${threshold}:${milestone}`);
      });
      if (progress >= 100) publish('bt_free_shipping_threshold_reached', { ...parameters, progress_percent: 100 }, `free-shipping:${threshold}:reached`);
    }

    render(subtotal) {
      const known=Boolean(this.department?.value && this.city?.value.trim());
      const zone=shippingZones[this.department?.value];
      const eligible=known && Boolean(zone);
      const threshold=60000000;
      const message=this.querySelector('.bt-free-shipping__message');
      const track=this.querySelector('.bt-free-shipping__track');
      const bar=this.querySelector('.bt-free-shipping__bar');
      if(message) message.textContent=!known ? 'Selecciona departamento e indica el municipio para consultar las condiciones de envío.' : !eligible ? 'Tu destino tiene envío pagado. El checkout mostrará la tarifa final.' : subtotal>=threshold ? 'Umbral alcanzado: envío gratis solo para productos del perfil general.' : `Agrega ${money(threshold-subtotal,this.dataset.currency)} más para obtener envío gratis en productos del perfil general.`;
      if(track) track.hidden=!eligible;
      if(bar) bar.style.setProperty('--bt-free-progress',`${Math.min(100,Math.max(0,subtotal/threshold*100))}%`);
      this.dataset.currentSubtotalCents=String(subtotal);
      this.dataset.addressKnown=String(known);
      this.dataset.primaryCity=String(eligible);
      this.dataset.zone=zone || 'PAID';
      this.dataset.state=!known?'unknown':!eligible?'paid':subtotal>=threshold?'reached':'progress';
      this.emitMilestones(subtotal,threshold);
    }
    static updateAll(cart) {
      // total_price: products after line/cart discounts, excluding shipping. No Addi fee is active.
      document.querySelectorAll('bt-free-shipping').forEach(element=>element.render(Number(cart.total_price || 0)));
    }
  }
  if(!customElements.get('bt-free-shipping'))customElements.define('bt-free-shipping',FreeShipping);
  const refreshShipping=()=>getCart().then(FreeShipping.updateAll).catch(()=>{});
  ['cart:update','cart:refresh','cart:change'].forEach(event=>document.addEventListener(event,refreshShipping));
  document.addEventListener('change',event=>{
    if(event.target.closest('cart-form,cart-drawer,#CartDrawer,[action*="/cart"]'))setTimeout(refreshShipping,450);
  });
  document.addEventListener('visibilitychange',()=>{if(!document.hidden)refreshShipping();});

  class B2BPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[role="dialog"]');
      this.closeButtons = this.querySelectorAll('[data-bt-b2b-close]');
      this.storageKey = 'bt-b2b-popup-next-display';
      this.preview = new URLSearchParams(location.search).get('bt_b2b_preview') === '1';
      this.onKeydown = this.onKeydown.bind(this);
      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close('button')));
      this.querySelectorAll('[data-bt-b2b-event]').forEach((link) => link.addEventListener('click', () => {
        publish(link.dataset.btB2bEvent, { page_type: pageType(), source_component: 'b2b_popup' });
        this.setCooldown();
      }));
      let blocked = false;
      try { blocked = Number(localStorage.getItem(this.storageKey) || 0) > Date.now(); } catch (error) {}
      if (!blocked || this.preview) this.timer = setTimeout(() => this.open(), this.preview ? 150 : Number(this.dataset.delayMs || 5000));
    }

    open() {
      if (!this.hidden) return;
      this.previouslyFocused = document.activeElement;
      this.hidden = false;
      this.isOpen = true;
      document.documentElement.classList.add('bt-b2b-popup-open');
      document.addEventListener('keydown', this.onKeydown);
      requestAnimationFrame(() => this.dialog?.focus());
      publish('bt_b2b_popup_view', { page_type: pageType(), source_component: 'b2b_popup' }, `b2b-view:${location.pathname}`);
    }

    close(closeMethod = 'unknown') {
      if (this.isOpen) publish('bt_b2b_popup_close', { page_type: pageType(), source_component: 'b2b_popup', close_method: closeMethod });
      this.isOpen = false;
      this.setCooldown();
      this.hidden = true;
      document.documentElement.classList.remove('bt-b2b-popup-open');
      document.removeEventListener('keydown', this.onKeydown);
      this.previouslyFocused?.focus?.();
    }

    setCooldown() {
      try { localStorage.setItem(this.storageKey, String(Date.now() + Number(this.dataset.cooldownDays || 7) * 864e5)); } catch (error) {}
    }

    onKeydown(event) {
      if (event.key === 'Escape') return this.close('escape');
      if (event.key !== 'Tab' || !this.dialog) return;
      const focusable = [...this.dialog.querySelectorAll('a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('keydown', this.onKeydown);
    }
  }

  if (!customElements.get('bt-b2b-popup')) customElements.define('bt-b2b-popup', B2BPopup);

  const observeCodWidgets = () => {
    document.querySelectorAll('bt-cod-benefit:not([data-bt-analytics-ready])').forEach((element) => {
      element.dataset.btAnalyticsReady = 'true';
      const context = [...element.classList].find((name) => name.startsWith('bt-cod--') && name !== 'bt-cod--compact')?.replace('bt-cod--', '') || 'unknown';
      const emitView = () => publish('bt_cod_widget_view', {
        page_type: pageType(), source_component: context,
        cart_value: Number(element.dataset.cartSubtotal || 0) / 100,
        cod_limit: Number(element.dataset.limit || 100000000) / 100,
      }, `cod-view:${location.pathname}:${context}`);
      if (window.IntersectionObserver) {
        const observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          emitView(); observer.disconnect();
        }, { threshold: 0.4 });
        observer.observe(element);
      } else emitView();
      element.querySelector('details')?.addEventListener('toggle', (event) => {
        if (event.currentTarget.open) publish('bt_cod_info_open', { page_type: pageType(), source_component: context });
      });
    });
  };

  observeCodWidgets();
  new MutationObserver(observeCodWidgets).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    const href = link.getAttribute('href') || '';
    if (!/(?:wa\.me|api\.whatsapp\.com|whatsapp\.com\/send)/i.test(href)) return;
    const sourceComponent = link.closest('bt-b2b-popup') ? 'b2b_popup' : link.dataset.btSource || 'whatsapp_link';
    publish('bt_whatsapp_click', {
      page_type: pageType(), product_sku: link.dataset.productSku || '',
      product_category: link.dataset.productCategory || '', source_component: sourceComponent,
    });
  });
})();
